import { useCallback, useState } from "react";
import * as Sentry from "@sentry/react";
import { Text } from "styles/utility-components";

import { DropdownMenu, DropdownMenuItem, DropdownMenuTrigger } from "components/ui/dropdown_menu";
import { toast } from "components/ui/toast/Toast";
import { useGetBidReportAnswers } from "lib/hooks/api/bid/answers/useGetBidReportAnswers";
import { BidReport } from "lib/hooks/api/bid/bid_reports/useBidReport";
import { useExportBidReportAsPDF } from "lib/hooks/api/bid/bid_reports/useExportBidReportAsPDF";
import { BidDetails } from "lib/hooks/api/bid/bids/useBid";
import { EventNames, useTracking } from "lib/tracking";
import { downloadFileFromLink, sanitizeFilename } from "lib/utils";
import { DROPDOWN_CLOSE_DELAY_MS } from "../common_report_components/utils";
import {
  exportQualificationReportAsCsv,
  exportQualificationReportAsDocx,
} from "./qualificationReportExportUtils";

type ShareQualificationReportDropdownProps = {
  bidId: string;
  bidName?: string;
  bidReportId: string;
  noticeGuid?: string;
  notice?: BidReport["notice"];
  winThemes: BidDetails["winThemes"];
  onCopyLink: () => Promise<boolean>;
};

export function ShareQualificationReportDropdown({
  bidId,
  bidName,
  bidReportId,
  noticeGuid,
  notice,
  winThemes,
  onCopyLink,
}: ShareQualificationReportDropdownProps) {
  const { logEvent } = useTracking();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLinkCopied, setIsLinkCopied] = useState(false);
  const [isDocxExporting, setIsDocxExporting] = useState(false);

  const { data: answersData } = useGetBidReportAnswers(bidReportId);

  const copyShareableUrl = async () => {
    const copied = await onCopyLink();
    if (copied) {
      setIsLinkCopied(true);
    }
  };

  const handleCSVExport = useCallback(() => {
    if (!answersData) return;
    try {
      exportQualificationReportAsCsv(answersData, winThemes, bidName);
      logEvent(EventNames.exportedData, {
        "Record GUID": noticeGuid,
        "Bid ID": bidId,
        "Bid report ID": bidReportId,
        "Action type": "Button clicked",
        "Page source": "Qualification report",
        "Data type": "Bid qualification report (csv)",
      });
    } catch (e) {
      Sentry.captureException(e);
      toast.error("Failed to export qualification report as CSV");
    }
  }, [answersData, winThemes, bidName, bidId, bidReportId, noticeGuid, logEvent]);

  const handleDocxExport = useCallback(async () => {
    if (!answersData) return;
    setIsDocxExporting(true);
    try {
      await exportQualificationReportAsDocx(answersData, winThemes, bidName, notice);
      logEvent(EventNames.exportedData, {
        "Record GUID": noticeGuid,
        "Bid ID": bidId,
        "Bid report ID": bidReportId,
        "Action type": "Button clicked",
        "Page source": "Qualification report",
        "Data type": "Bid qualification report (docx)",
      });
    } catch (e) {
      Sentry.captureException(e);
      toast.error("Failed to export qualification report as Word document");
    } finally {
      setIsDocxExporting(false);
    }
  }, [answersData, winThemes, bidName, notice, bidId, bidReportId, noticeGuid, logEvent]);

  const { mutate: exportAsPDF, isLoading: isPdfExporting } = useExportBidReportAsPDF(bidReportId, {
    onSuccess: (url) => {
      if (!url) {
        toast.error("Failed to export qualification report as PDF");
        return;
      }
      const fileName = `Qualification_report_${sanitizeFilename(bidName || "")}.pdf`;
      downloadFileFromLink(url, fileName);
      logEvent(EventNames.exportedData, {
        "Record GUID": noticeGuid,
        "Bid ID": bidId,
        "Bid report ID": bidReportId,
        "Action type": "Button clicked",
        "Page source": "Qualification report",
        "Data type": "Bid qualification (pdf)",
      });
    },
    onError: () => {
      toast.error("Failed to export qualification report as PDF");
    },
  });

  return (
    <DropdownMenu
      trigger={
        <DropdownMenuTrigger icon="share07" buttonType="secondary" aria-label="Share report">
          Share
        </DropdownMenuTrigger>
      }
      open={isDropdownOpen}
      onOpenChange={(open) => {
        if (!open) {
          setTimeout(() => {
            setIsDropdownOpen(false);
            setIsLinkCopied(false);
          }, DROPDOWN_CLOSE_DELAY_MS);
        } else {
          setIsDropdownOpen(open);
        }
      }}
    >
      {isLinkCopied ? (
        <DropdownMenuItem icon="checkOutlined" iconProps={{ color: "sysPrimaryDefault" }}>
          <Text color="sysPrimaryDefault">Link copied</Text>
        </DropdownMenuItem>
      ) : (
        <DropdownMenuItem icon="link01" onClick={copyShareableUrl}>
          Copy shareable link
        </DropdownMenuItem>
      )}
      <DropdownMenuItem icon="download01" disabled={!answersData?.length} onClick={handleCSVExport}>
        Download CSV
      </DropdownMenuItem>
      <DropdownMenuItem
        icon={isDocxExporting ? "spinner" : "fileDownload02"}
        loading={isDocxExporting}
        disabled={isDocxExporting || !answersData?.length}
        onClick={handleDocxExport}
      >
        Download DOCX
      </DropdownMenuItem>
      <DropdownMenuItem
        icon={isPdfExporting ? "spinner" : "fileDownload02"}
        loading={isPdfExporting}
        disabled={isPdfExporting}
        onClick={() => exportAsPDF()}
      >
        Download PDF
      </DropdownMenuItem>
    </DropdownMenu>
  );
}
